import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  DepartmentService,
  DepartmentSummary,
  DepartmentDetail,
  Asset,
  AuditLog,
  IssueLog,
  PaginationMeta,
  DepartmentFilterParams,
  AxiosErrorResponse,
} from "@/lib/api/services/departmentService";

export interface DepartmentState {
  departments: DepartmentSummary[];
  departmentsPagination?: PaginationMeta;

  currentDepartment: DepartmentDetail | null;

  departmentAssets: Asset[];
  departmentAssetsPagination?: PaginationMeta;

  departmentAuditLogs: AuditLog[];
  departmentAuditLogsPagination?: PaginationMeta;

  departmentIssues: IssueLog[];
  departmentIssuesPagination?: PaginationMeta;

  departmentAssetAuditLogs: AuditLog[];
  departmentAssetAuditLogsPagination?: PaginationMeta;

  departmentAssetIssues: IssueLog[];
  departmentAssetIssuesPagination?: PaginationMeta;

  isLoading: boolean;
  error: string | null;
  selectedOrganizationId: number | null;
  selectedSiteId: number | null;
  selectedDepartmentId: number | null;
  selectedAssetId: number | null;
  searchTerm?: string;
}

const defaultInitialState: DepartmentState = {
  departments: [],
  departmentsPagination: undefined,
  currentDepartment: null,
  departmentAssets: [],
  departmentAssetsPagination: undefined,
  departmentAuditLogs: [],
  departmentAuditLogsPagination: undefined,
  departmentIssues: [],
  departmentIssuesPagination: undefined,
  departmentAssetAuditLogs: [],
  departmentAssetAuditLogsPagination: undefined,
  departmentAssetIssues: [],
  departmentAssetIssuesPagination: undefined,
  isLoading: false,
  error: null,
  selectedOrganizationId: null,
  selectedSiteId: null,
  selectedDepartmentId: null,
  selectedAssetId: null,
  searchTerm: undefined,
};

const initialState: DepartmentState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

/**
 * Get all departments with summary analytics
 * Returns list of departments with health, assets count, value, compliance, issues
 */
export const getDepartmentsSummary = createAsyncThunk(
  "department/getDepartmentsSummary",
  async (
    {
      organizationId,
      siteId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentsSummary(
        organizationId,
        siteId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get detailed analytics for a specific department
 * Returns department details with breakdown of health status, compliance, asset states, issues
 */
export const getDepartmentDetails = createAsyncThunk(
  "department/getDepartmentDetails",
  async (
    {
      organizationId,
      siteId,
      departmentId,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentDetails(
        organizationId,
        siteId,
        departmentId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get all assets in a specific department
 * Returns assets with all relations and pagination
 */
export const getDepartmentAssets = createAsyncThunk(
  "department/getDepartmentAssets",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentAssets(
        organizationId,
        siteId,
        departmentId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get all audit logs for assets in a specific department
 * Returns audit logs with pagination and search support
 */
export const getDepartmentAuditLogs = createAsyncThunk(
  "department/getDepartmentAuditLogs",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentAuditLogs(
        organizationId,
        siteId,
        departmentId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get all issues for assets in a specific department
 * Returns issues with pagination, search, and status filter support
 */
export const getDepartmentIssues = createAsyncThunk(
  "department/getDepartmentIssues",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentIssues(
        organizationId,
        siteId,
        departmentId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get audit logs for a specific asset in a department
 * Returns audit logs with pagination and search support
 */
export const getDepartmentAssetAuditLogs = createAsyncThunk(
  "department/getDepartmentAssetAuditLogs",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      assetId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      assetId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentAssetAuditLogs(
        organizationId,
        siteId,
        departmentId,
        assetId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Get issues for a specific asset in a department
 * Returns issues with pagination, search, and status filter support
 */
export const getDepartmentAssetIssues = createAsyncThunk(
  "department/getDepartmentAssetIssues",
  async (
    {
      organizationId,
      siteId,
      departmentId,
      assetId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      departmentId: number;
      assetId: number;
      params?: DepartmentFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await DepartmentService.getDepartmentAssetIssues(
        organizationId,
        siteId,
        departmentId,
        assetId,
        params,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const departmentSlice = createSlice({
  name: "department",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<number>) => {
      state.selectedOrganizationId = action.payload;
    },
    setSelectedSite: (state, action: PayloadAction<number>) => {
      state.selectedSiteId = action.payload;
    },
    setSelectedDepartment: (state, action: PayloadAction<number>) => {
      state.selectedDepartmentId = action.payload;
    },
    setSelectedAsset: (state, action: PayloadAction<number>) => {
      state.selectedAssetId = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetDepartments: () => {
      return defaultInitialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentsSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentsSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = action.payload.departments;
        state.departmentsPagination = action.payload.pagination;
      })
      .addCase(getDepartmentsSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDepartment = action.payload;
      })
      .addCase(getDepartmentDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentAssets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentAssets = action.payload.assets;
        state.departmentAssetsPagination = action.payload.pagination;
      })
      .addCase(getDepartmentAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentAuditLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentAuditLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentAuditLogs = action.payload.logs;
        state.departmentAuditLogsPagination = action.payload.pagination;
      })
      .addCase(getDepartmentAuditLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentIssues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentIssues.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentIssues = action.payload.issues;
        state.departmentIssuesPagination = action.payload.pagination;
      })
      .addCase(getDepartmentIssues.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentAssetAuditLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentAssetAuditLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentAssetAuditLogs = action.payload.logs;
        state.departmentAssetAuditLogsPagination = action.payload.pagination;
      })
      .addCase(getDepartmentAssetAuditLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getDepartmentAssetIssues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentAssetIssues.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentAssetIssues = action.payload.issues;
        state.departmentAssetIssuesPagination = action.payload.pagination;
      })
      .addCase(getDepartmentAssetIssues.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrganization,
  setSelectedSite,
  setSelectedDepartment,
  setSelectedAsset,
  setSearchTerm,
  clearError,
  resetDepartments,
} = departmentSlice.actions;

export default departmentSlice.reducer;
